// src/pages/TermsOfService.js
import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Header, Body } from "../styleguide/atoms/index.js";
import * as styles from "../styleguide/styles/Stylesheet.js";

const PageShell = styled.div`
  width: 100%;
  background: ${styles.color_white};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Container = styled.div`
  width: 100%;
  max-width: ${styles.width_md};
  padding: 64px ${styles.spacing_standard} 96px;
`;

const Section = styled.section`
  margin: 32px 0;
`;

const Small = styled.div`
  font-family: ${styles.font_sans};
  font-size: 13px;
  color: ${styles.color_gry_4};
`;

const List = styled.ul`
  padding-left: 1.25rem;
  margin: 8px 0 0;
  li {
    margin: 6px 0;
  }
`;

const LinkA = styled.a`
  color: ${styles.color_primary};
  text-decoration: underline;
`;

const NavLinkWrapper = styled.div`
  padding: 0 0 ${styles.spacing_standard};
`;

const NavLink = styled(Link)`
  color: ${styles.color_accent};
  font-family: ${styles.font_sans};
  font-size: 16px;
  transition: all 250ms;
  &:hover {
    color: ${styles.color_accent_dark};
  }
`;

export default function TermsOfService() {
  const LAST_UPDATED = "November 29, 2025";
  const contactEmail = "summercampslashergame@gmail.com";
  const siteNames = ["summercampslasher.com", "stage.summercampslasher.com"];

  const handleNavClick = (e) => e.stopPropagation?.();

  return (
    <PageShell>
      <Container>
        <NavLinkWrapper>
          <NavLink to="/" onClick={handleNavClick}>
            Go Home
          </NavLink>
        </NavLinkWrapper>

        <Header type="h1" padding={`0 0 ${styles.spacing_standard_half}`}>
          Terms of Service
        </Header>
        <Small>Last updated: {LAST_UPDATED}</Small>

        <Section>
          <Body>
            Welcome to <strong>Summer Camp Slasher</strong> (the “Service”),
            operated at <strong>{siteNames[0]}</strong> and{" "}
            <strong>{siteNames[1]}</strong>. By accessing or using the Service,
            you agree to these Terms of Service (the “Terms”). If you do not
            agree, do not use the Service. For information about how we collect
            and use data, see our <Link to="/privacy">Privacy Policy</Link>.
          </Body>
        </Section>

        <Section>
          <Header type="h2">1. Who can use the Service</Header>
          <Body>
            <List>
              <li>
                <strong>Age.</strong> The Service is not intended for children
                under 13. Do not use the Service if you are under 13.
              </li>
              <li>
                <strong>Region.</strong> You are responsible for ensuring the
                Service is permitted where you live.
              </li>
            </List>
          </Body>
        </Section>

        <Section>
          <Header type="h2">2. Accounts & anonymity</Header>
          <Body>
            The game uses <em>Firebase Authentication (anonymous sign-in)</em>.
            You receive an anonymous identifier (“UID”) that lets you join and
            play games without providing a name or email. You are responsible
            for actions taken with your UID while using the Service.
          </Body>
        </Section>

        <Section>
          <Header type="h2">3. Gameplay & user content</Header>
          <Body>
            You may submit or display content (e.g., player names, chat,
            messages) (“User Content”). You represent you have the right to
            submit it and it does not violate law or rights of others. You grant
            us a worldwide, non-exclusive, royalty-free license to host, store,
            transmit, display, and otherwise use your User Content solely to
            operate, improve, and protect the Service.
          </Body>
        </Section>

        <Section>
          <Header type="h2">4. Tips and supporter payments</Header>
          <Body>
            The Service may offer an option to send voluntary tips or supporter
            payments. These are:
            <List>
              <li>Entirely optional and not required to use the game.</li>
              <li>
                Processed by third-party payment providers (such as Stripe,
                PayPal, or Ko-fi), whose terms and privacy policies apply to
                those transactions.
              </li>
              <li>
                Generally non-refundable, unless we are required by applicable
                law to provide a refund.
              </li>
            </List>
            We do not receive or store your full payment card details.
          </Body>
        </Section>

        <Section>
          <Header type="h2">5. Acceptable use</Header>
          <Body>
            You agree not to:
            <List>
              <li>
                Harass, threaten, or engage in hate speech or unlawful content.
              </li>
              <li>
                Cheat, exploit bugs, or interfere with gameplay or
                infrastructure.
              </li>
              <li>
                Attempt to reverse engineer, overload, or attack the Service.
              </li>
              <li>Upload malicious code or infringe third-party rights.</li>
            </List>
            We may monitor or moderate public areas (e.g., in-game chat) to
            maintain a safe experience.
          </Body>
        </Section>

        <Section>
          <Header type="h2">6. Moderation & enforcement</Header>
          <Body>
            We or a game host may take appropriate action (mute, kick, ban from
            a lobby, or restrict access) at our discretion if these Terms are
            violated or to protect players. Enforcement may rely on your
            anonymous UID or session identifiers; we are not required to notify
            you prior to action.
          </Body>
        </Section>

        <Section>
          <Header type="h2">7. Intellectual property</Header>
          <Body>
            The Service, including code, artwork, and content, is owned by us or
            our licensors and protected by applicable laws. Except for your User
            Content and rights expressly granted to you, no rights are
            transferred. Do not use our trademarks or assets without permission.
          </Body>
        </Section>

        <Section>
          <Header type="h2">8. Third-party services</Header>
          <Body>
            The Service uses third-party providers, including:
            <List>
              <li>
                <strong>Firebase (Google Cloud)</strong> for hosting,
                authentication, database, and functions.
              </li>
              <li>
                <strong>Contentful</strong> for character art and text delivery.
              </li>
            </List>
            Their terms and privacy policies apply to their components of the
            Service.
          </Body>
        </Section>

        <Section>
          <Header type="h2">9. Availability & changes</Header>
          <Body>
            The Service may change or be unavailable at times. We may modify or
            discontinue features at any time. We may update these Terms by
            posting a new version with an updated “Last updated” date.
          </Body>
        </Section>

        <Section>
          <Header type="h2">10. Disclaimers</Header>
          <Body>
            THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM
            EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR
            IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, AND NON-INFRINGEMENT.
          </Body>
        </Section>

        <Section>
          <Header type="h2">11. Limitation of liability</Header>
          <Body>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE ARE NOT LIABLE FOR
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
            OR ANY LOSS OF DATA, PROFITS, OR REVENUE, ARISING FROM OR RELATED TO
            YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM WILL NOT
            EXCEED USD $50.
          </Body>
        </Section>

        <Section>
          <Header type="h2">12. Indemnification</Header>
          <Body>
            You agree to defend, indemnify, and hold us harmless from claims or
            liabilities arising from your misuse of the Service or violation of
            these Terms.
          </Body>
        </Section>

        <Section>
          <Header type="h2">13. Termination</Header>
          <Body>
            You may stop using the Service at any time. We may suspend or
            terminate access (including specific lobbies) if we believe you have
            violated these Terms or pose a risk to the Service or other users.
          </Body>
        </Section>

        <Section>
          <Header type="h2">14. Governing law</Header>
          <Body>
            These Terms are governed by the laws of the State of Oklahoma, USA,
            without regard to conflict-of-law principles. Venue for disputes
            lies in the state or federal courts located in Tulsa County,
            Oklahoma.
          </Body>
        </Section>

        <Section>
          <Header type="h2">15. Contact</Header>
          <Body>
            Questions about these Terms? Email{" "}
            <LinkA href={`mailto:${contactEmail}`}>{contactEmail}</LinkA>.
          </Body>
        </Section>

        <Section>
          <Small>
            <em>Note:</em> This page is informational and not legal advice.
          </Small>
        </Section>
      </Container>
    </PageShell>
  );
}
